import type { IndustryPage } from "@/lib/data";

export const landscapers: IndustryPage = {
  slug: "landscapers",
  seoTitle:
    "Landscaping Website Design UK | Show Off Your Gardens",
  seoDescription:
    "Landscaping website design with big project galleries, before-and-after photos, service areas and quote forms. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for landscapers",
  tldr:
    "ConstructionWebDesign.uk offers landscaping website design for UK landscapers and garden designers: big project galleries, before-and-after photos, a page for each service and the BALI or APL details you hold. Each site costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Your website is live in 7 days, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Websites for landscapers · UK",
    headline: "Website design for landscapers that shows off every garden.",
    highlight: "shows off every garden",
    sub: "Websites for UK landscapers with full-width project galleries, before-and-after sliders, a page for each hard and soft landscaping service, service area pages, a design consultation and quote form, the BALI, APL or TrustMark badges you hold and on-page local SEO.",
    secondaryCta: { label: "See landscaper demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Landscapers.jpg",
      alt: "Landscaper laying a porcelain patio and stone retaining wall on a domestic garden build",
      headlineTop: "Win every premium",
      headlineBottom: "garden in your patch.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/hedgerow.jpg",
    },
  ],
  demoCategory: "Landscapers",
  demosHeading: "Landscaper website demo templates.",
  demosSub:
    "Demo templates for landscapers and garden design firms. Open one to see the layout, galleries and pages a landscaping website can include. These are demos, not client sites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a landscaper need a website?",
    intro:
      "A landscaper needs a website because homeowners want to see finished gardens before they ask for a quote. A landscaper website puts your best projects, your services, the areas you cover and your accreditations in one place, and takes enquiries while you're on site.",
    points: [
      {
        title: "Let the gardens do the selling.",
        body: "Landscaping is visual work. A mobile-first site that shows your photos large and sharp gives a homeowner a clear sense of your design style and the standard of your build before they pick up the phone.",
        icon: "Tree",
      },
      {
        title: "Give every project its own page.",
        body: "Each garden, patio, driveway or planting scheme can have its own project page with photos, the brief and what was built. A project page named by job type and town gives Google something specific to match when a homeowner searches for that kind of work nearby.",
        icon: "Sparkle",
      },
      {
        title: "Get found for \"landscaper near me\".",
        body: "On-page local SEO, a page for each area you cover and a link to your Google Business Profile help Google match your website to landscaping searches in your patch.",
        icon: "Pin",
      },
      {
        title: "Show the trust signals homeowners check.",
        body: "BALI or APL membership, TrustMark registration, your insurance details and your Google reviews sit where visitors look first, with registration numbers linked to the official register. Only the accreditations you actually hold go on the site.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does good landscaping website design include?",
    intro:
      "A good landscaper website puts large project photos first, then gives each service, each area you cover and each accreditation you hold a clear place, with an easy way to ask for a quote. Every landscaper website from ConstructionWebDesign.uk is planned around the parts below.",
    items: [
      {
        title: "Big project galleries at full width.",
        body: "Your photos run full width, at the size the work deserves. Each finished garden gets its own project page with photos, the brief, the materials used and the planting, so a homeowner can see the kind of job you do best.",
      },
      {
        title: "Before-and-after photos on every project.",
        body: "A drag slider shows the overgrown or bare original next to the finished garden. Before-and-after photos show the change in a way a single finished shot can't, and they work well on a phone screen.",
      },
      {
        title: "A page for every hard and soft landscaping service.",
        body: "Garden design, hard landscaping (patios, paths, walls, driveways, decking, fencing) and soft landscaping (turfing, planting, borders) can each have their own page, along with water features and garden lighting. Each page targets its own search, so someone looking for a porcelain patio lands on your patio page, not a general list. A driveways page can also explain permeable paving: in England, a new front garden driveway over 5 square metres needs planning permission unless water can drain through it or run off into a lawn or border.",
      },
      {
        title: "Design consultation and quote forms.",
        body: "The enquiry form asks for a postcode, the rough size of the garden, a budget band and a few photos, and lets the homeowner ask for a design consultation or a site visit. You have what you need to plan the visit before you ring back.",
      },
      {
        title: "Service area pages for the towns you cover.",
        body: "A page for each town or district you work in tells homeowners, and Google, where you take on work. Each area page can show nearby projects and the services you offer there.",
      },
      {
        title: "BALI, APL and TrustMark badges near the top.",
        body: "BALI (the British Association of Landscape Industries) and APL (the Association of Professional Landscapers) are trade bodies for UK landscaping firms, and both list their members online. If you hold BALI or APL membership, TrustMark registration or Marshalls Register accreditation, the badge sits in the header with your registration linked to the official register. Anything you don't hold stays off the site.",
      },
      {
        title: "Local SEO built in from day one.",
        body: "Every landscaper website includes on-page local SEO: schema markup, area pages, town and service keywords in page titles and a link to your Google Business Profile.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a landscaping website cost in the UK?",
      a: "A landscaping website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer billing £50–£120 an hour, £5,000–£25,000 for an agency, or about £22 a month for Wix or Squarespace. You own the website.",
    },
    {
      q: "How long does it take to build a landscaping website?",
      a: "A landscaping website from ConstructionWebDesign.uk goes live 7 days after the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. On the discovery call you explain the garden work you want more of, the services you offer and the areas you cover. For comparison, a typical UK agency project takes 8–12 weeks.",
    },
    {
      q: "What should a landscaper website include?",
      a: "A landscaper website should include large project galleries, before-and-after photos, a page for each service (garden design, hard landscaping, soft landscaping, patios, driveways, decking, fencing), pages for the areas you cover, your reviews and a quote form that asks for photos and a postcode. BALI, APL or TrustMark badges belong near the top if you hold them. ConstructionWebDesign.uk agrees the sitemap with you before design starts.",
    },
    {
      q: "Do landscapers need a website?",
      a: "Yes, landscapers need a website, because a homeowner planning a new garden wants to see finished work before asking for a quote. Websites for landscapers show project photos, services, the areas covered and accreditations in one place. Instagram and directory profiles help, but they sit on someone else's platform. Your own website shows only your business, takes enquiries while you're on site and belongs to you.",
    },
    {
      q: "How do landscapers get found on Google?",
      a: "Landscapers get found on Google through a website with a page for each service and area, on-page local SEO and a complete Google Business Profile with regular reviews. Web design for landscapers from ConstructionWebDesign.uk includes on-page local SEO: schema markup, area pages, town and service keywords in page titles and a link to your Google Business Profile. Nobody can honestly guarantee a ranking, but these are the basics a landscaper needs in place.",
    },
    {
      q: "Can I show before-and-after garden photos on my website?",
      a: "Yes, every landscaper website from ConstructionWebDesign.uk can show before-and-after garden photos. Each project page can have a drag slider with the original garden on one side and the finished work on the other, plus the brief, the materials and the planting. Before-and-after photos taken from the same spot, in similar light, show the change most clearly.",
    },
    {
      q: "Should a landscaper show BALI or APL membership on their website?",
      a: "Yes, a landscaper who holds BALI or APL membership should show it on the website, linked to the official member listing. BALI (the British Association of Landscape Industries) and APL (the Association of Professional Landscapers) both list their members online, so a homeowner can check the badge. Landscaper website design from ConstructionWebDesign.uk puts these badges near the top of the page. Firms that aren't members leave them off.",
    },
    {
      q: "Can I build my own landscaping website on Wix or Squarespace?",
      a: "Yes, a landscaper can build a website on Wix, Squarespace or WordPress, and Wix or Squarespace typically cost about £22 a month. The trade-off is time: you set up the galleries, write the service pages and add local SEO yourself, and a Wix or Squarespace site goes offline if you stop paying. ConstructionWebDesign.uk offers website design for landscapers at a fixed £1,000, and you own the result.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
    {
      href: "/blog/local-seo-for-builders",
      label: "Local SEO for builders: rank for \"builder near me\"",
    },
  ],
  finalCta: {
    headline: "Talk to us about your landscaping website.",
    highlight: "landscaping website",
    sub: "Book a free 30-minute call or email hello@constructionwebdesign.uk. Fixed £1,000, one-off, and your website is live in 7 days.",
  },
};
